import { type StreamingStateManager } from '@databases/database-ai-first-creation-state';
import {
  CsvErrorCategory,
  type CsvErrorCollector,
  CsvErrorType,
  type RemoveEntryToken,
  type EditEntryToken,
} from '@databases/streaming-csv-parser';
import { generateId } from '@databases/utils/utils';

import { type DatabaseAgent } from '../agents/database-agent';

import { type ValueProcessor } from './value-processor';

export class EntryProcessor {
  constructor(
    private readonly agent: DatabaseAgent,
    private readonly streamingState: StreamingStateManager,
    private readonly valueProcessor: ValueProcessor,
    private rowIdToEntryIdMappings: Map<string, string>,
    private readonly errorCollector?: CsvErrorCollector
  ) {}

  private addError(message: string): void {
    this.errorCollector?.addError(
      message,
      CsvErrorCategory.ENTRY,
      CsvErrorType.VALIDATION
    );
  }

  // TODO: consider view settings when creating an entry. We may need to set default values.
  // https://product-fabric.atlassian.net/browse/CTD-5098
  public handleAddEntryToken() {
    const entry = {
      id: generateId(),
      values: {},
    };
    this.agent.addEntry(entry);
    this.valueProcessor.setCurrentEntryId(entry.id);
  }

  public handleEditEntryToken(editEntryToken: EditEntryToken) {
    const entryId = this.rowIdToEntryIdMappings.get(String(editEntryToken.rowNumber));

    if (!entryId) {
      this.addError(
        `Entry not found for row ${editEntryToken.rowNumber}. Cannot edit non-existent entry.`
      );
      return;
    }

    this.streamingState.setEntryOperation('edit');
    this.valueProcessor.setCurrentEntryId(entryId);
  }

  public handleRemoveEntryToken(removeEntryToken: RemoveEntryToken) {
    const entryId = this.rowIdToEntryIdMappings.get(String(removeEntryToken.rowNumber));

    if (!entryId) {
      this.addError(
        `Entry not found for row ${removeEntryToken.rowNumber}. Cannot remove non-existent entry.`
      );
      return;
    }

    this.valueProcessor.setCurrentEntryId(entryId);
    this.streamingState.scheduleEntryDeletion(entryId, () => {
      this.agent.removeEntry(entryId);
      this.valueProcessor.clearCurrentEntryId();
    });
  }
}
